<script setup>
import { ref, reactive, onBeforeUnmount } from 'vue';
import { Switch } from '@headlessui/vue';
import { PlayIcon, StopIcon } from '@heroicons/vue/20/solid';
import SettingsCard from './SettingsCard.vue';
import SettingsDropdown from './SettingsDropdown.vue';
import SoundWaveform from './SoundWaveform.vue';
import useAudioSettings from '../../composables/useAudioSettings';
import {
  ROLES, soundOptions, soundSetInfo, roleFile, previewSound, stopPreview as stopPreviewSound,
} from '../../utils/matchmakingSounds';
import { t } from '../../i18n';

// How long a preview plays: a loop this long, a one-shot its own length, or this until measured.
const LOOP_PREVIEW_MS = 4000;
const ONE_SHOT_PREVIEW_MS = 2500;

const {
  settings, updateSound, restoreDefaults: resetSettings, tryOut,
} = useAudioSettings();

// In the order matchmaking plays them
const roles = Object.keys(ROLES);
const soundName = (role) => t(`profile.settings.audio.sounds.${role}`);
const options = (role) => soundOptions(role)
  .map((value) => ({ value, label: soundSetInfo(value).name }));

// The role a preview is playing, for its button and waveform
const previewing = ref(null);
let previewTimer = null;
// By role, how long its sound is, once its waveform has measured it
const durations = reactive({});

// By role, a volume being dragged and not yet saved
const dragging = reactive({});

const volumeOf = (role) => dragging[role] ?? settings.value.sounds[role].volume;
const percent = (role) => Math.round(volumeOf(role) * 100);
const isMuted = (role) => settings.value.sounds[role].muted;

function dragVolume(role, value) {
  dragging[role] = value / 100;
  tryOut(role, { volume: dragging[role] });
}

async function saveVolume(role, value) {
  const volume = value / 100;
  dragging[role] = volume;
  await updateSound(role, { volume });
  // Unless a newer drag has started since
  if (dragging[role] === volume) delete dragging[role];
}

function stopPreview() {
  clearTimeout(previewTimer);
  if (previewing.value) stopPreviewSound(0.2);
  previewing.value = null;
}

function previewLength(role) {
  if (ROLES[role].loop) return LOOP_PREVIEW_MS;
  return durations[role] ? durations[role] * 1000 + 150 : ONE_SHOT_PREVIEW_MS;
}

function preview(role) {
  const wasPlaying = previewing.value === role;
  stopPreview();
  if (wasPlaying || isMuted(role)) return;
  previewing.value = role;
  previewSound(role);
  previewTimer = setTimeout(stopPreview, previewLength(role));
}

// A one-shot previewing before its length was known plays it out in full once it is.
function measured(role, seconds) {
  durations[role] = seconds;
  if (previewing.value === role && !ROLES[role].loop) {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(stopPreview, previewLength(role));
  }
}

function setMuted(role, muted) {
  if (muted && previewing.value === role) stopPreview();
  updateSound(role, { muted });
}

// Saves the pick and plays it, so the player hears what they chose.
function pickSound(role, sound) {
  stopPreview();
  delete durations[role];
  // Into the sounds now, not when the watcher next runs, so the preview below plays the pick
  tryOut(role, { sound });
  updateSound(role, { sound });
  if (!isMuted(role)) preview(role);
}

function restoreDefaults() {
  stopPreview();
  resetSettings();
}

onBeforeUnmount(stopPreview);
</script>

<template>
  <SettingsCard
    :title="$t('profile.settings.audio.sounds_title')"
    :description="$t('profile.settings.audio.sounds_text')"
  >
    <template #actions>
      <button
        type="button"
        class="self-start rounded-md bg-gray-800 px-3 py-2 text-xs font-medium text-text-primary duration-200 hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:self-auto"
        @click="restoreDefaults"
      >
        {{ $t('profile.settings.audio.restore_defaults') }}
      </button>
    </template>

    <ol class="px-6 py-2">
      <li
        v-for="(role, index) in roles"
        :key="role"
        class="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 py-4 lg:grid-cols-[1.25rem_11rem_minmax(0,1fr)_8.5rem_10rem_auto]"
      >
        <!-- Where this sound falls in matchmaking: a node on the line through every sound -->
        <span class="relative flex justify-center self-stretch" aria-hidden="true">
          <span
            class="absolute hidden w-px bg-gray-800 lg:block"
            :class="[
              index === 0 ? 'top-1/2' : '-top-4',
              index === roles.length - 1 ? 'bottom-1/2' : '-bottom-4',
            ]"
          />
          <span
            class="relative mt-[0.35rem] h-2.5 w-2.5 self-start rounded-full border-2 duration-200 lg:mt-0 lg:self-center"
            :class="isMuted(role) ? 'border-gray-600 bg-gray-900' : 'border-primary-500 bg-primary-500'"
          />
        </span>

        <div class="flex min-w-0 flex-col gap-0.5">
          <span
            class="text-sm font-medium duration-200"
            :class="isMuted(role) ? 'text-text-secondary' : 'text-text-primary'"
          >
            {{ soundName(role) }}
          </span>
          <span class="text-xs text-text-secondary">
            {{ $t(`profile.settings.audio.moments.${role}`) }}
          </span>
        </div>

        <Switch
          :model-value="!isMuted(role)"
          :class="isMuted(role) ? 'bg-gray-700' : 'bg-primary-600'"
          class="relative inline-flex h-5 w-9 min-w-9 items-center rounded-full duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 lg:order-last"
          @update:model-value="(on) => setMuted(role, !on)"
        >
          <span class="sr-only">
            {{ $t('profile.settings.audio.toggle_sr', { sound: soundName(role) }) }}
          </span>
          <span
            :class="isMuted(role) ? 'translate-x-[2px]' : 'translate-x-[18px]'"
            class="inline-block h-4 w-4 transform rounded-full bg-white transition"
          />
        </Switch>

        <button
          type="button"
          :disabled="isMuted(role)"
          :aria-label="previewing === role
            ? $t('profile.settings.audio.stop_preview')
            : $t('profile.settings.audio.preview', { sound: soundName(role) })"
          class="group col-start-2 col-end-4 flex h-11 items-center gap-3 rounded-md px-2 text-text-secondary duration-200 hover:bg-gray-800/60 hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:hover:bg-transparent lg:col-auto"
          @click="preview(role)"
        >
          <span
            class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full duration-200"
            :class="previewing === role
              ? 'bg-primary-500 text-white'
              : 'bg-gray-800 group-hover:bg-gray-700 group-disabled:bg-gray-800'"
          >
            <StopIcon v-if="previewing === role" class="h-3.5 w-3.5" aria-hidden="true" />
            <PlayIcon v-else class="ml-px h-3.5 w-3.5" aria-hidden="true" />
          </span>
          <span class="h-8 min-w-0 flex-1">
            <SoundWaveform
              :src="roleFile(role, settings.sounds[role].sound)"
              :volume="volumeOf(role)"
              :muted="isMuted(role)"
              :playing="previewing === role"
              :loop="!!ROLES[role].loop"
              @measured="(seconds) => measured(role, seconds)"
            />
          </span>
        </button>

        <div class="col-start-2 lg:col-auto">
          <SettingsDropdown
            :model-value="settings.sounds[role].sound"
            :options="options(role)"
            :disabled="isMuted(role) || options(role).length < 2"
            :label="$t('profile.settings.audio.sound_sr', { sound: soundName(role) })"
            @update:model-value="(sound) => pickSound(role, sound)"
          />
        </div>

        <div
          class="col-start-3 flex items-center gap-3 lg:col-auto"
          :class="{ 'opacity-40': isMuted(role) }"
        >
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            :value="percent(role)"
            :disabled="isMuted(role)"
            :aria-label="$t('profile.settings.audio.volume_sr', { sound: soundName(role) })"
            class="volume-slider w-24 min-w-0 flex-1"
            :style="{ '--fill': `${percent(role)}%` }"
            @input="dragVolume(role, $event.target.value)"
            @change="saveVolume(role, $event.target.value)"
          />
          <span class="w-9 text-right text-xs tabular-nums text-text-secondary">
            {{ percent(role) }}%
          </span>
        </div>
      </li>
    </ol>
  </SettingsCard>
</template>

<style scoped>
.volume-slider {
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    theme('colors.primary.500') var(--fill),
    theme('colors.gray.700') var(--fill)
  );
  cursor: pointer;
}

.volume-slider:disabled {
  cursor: not-allowed;
}

.volume-slider:focus-visible {
  outline: 2px solid theme('colors.primary.500');
  outline-offset: 6px;
}

.volume-slider::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 0 0 3px theme('colors.gray.900');
}

.volume-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border: 0;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 0 0 3px theme('colors.gray.900');
}
</style>
